/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_DEFERRED: 'transition/request-payment-deferred-payout',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  REQUEST_PAYMENT_AFTER_INQUIRY_DEFERRED:
    'transition/request-payment-after-inquiry-deferred-payout',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CONFIRM_PAYMENT_DEFERRED: 'transition/confirm-payment-deferred-payout',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',
  EXPIRE_PAYMENT_DEFERRED: 'transition/expire-payment-deferred-payout',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',
  ACCEPT_DEFERRED: 'transition/accept-deferred-payout',
  DECLINE_DEFERRED: 'transition/decline-deferred-payout',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',
  OPERATOR_ACCEPT_DEFERRED: 'transition/operator-accept-deferred-payout',
  OPERATOR_DECLINE_DEFERRED: 'transition/operator-decline-deferred-payout',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',
  EXPIRE_DEFERRED: 'transition/expire-deferred-payout',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',
  CANCEL_DEFERRED: 'transition/cancel-deferred-payout',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  COMPLETE_DEFERRED: 'transition/complete-deferred-payout',
  OPERATOR_COMPLETE_DEFERRED: 'transition/operator-complete-deferred-payout',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PENDING_PAYMENT_DEFERRED: 'pending-payment-deferred',
  PAYMENT_EXPIRED: 'payment-expired',
  PAYMENT_EXPIRED_DEFERRED: 'payment-expired-deferred',
  PREAUTHORIZED: 'preauthorized',
  PREAUTHORIZED_DEFERRED: 'preauthorized-deferred',
  DECLINED: 'declined',
  DECLINED_DEFERRED: 'declined-deferred',
  ACCEPTED: 'accepted',
  ACCEPTED_DEFERRED: 'accepted-deferred',
  EXPIRED: 'expired',
  EXPIRED_DEFERRED: 'expired-deferred',
  CANCELED: 'canceled',
  CANCELED_DEFERRED: 'canceled-deferred',
  DELIVERED: 'delivered',
  DELIVERED_DEFERRED: 'delivered-deferred',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_DEFERRED]: states.PENDING_PAYMENT_DEFERRED,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_DEFERRED]: states.PENDING_PAYMENT_DEFERRED,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PENDING_PAYMENT_DEFERRED]: {
      on: {
        [transitions.EXPIRE_PAYMENT_DEFERRED]: states.PAYMENT_EXPIRED_DEFERRED,
        [transitions.CONFIRM_PAYMENT_DEFERRED]: states.PREAUTHORIZED_DEFERRED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PAYMENT_EXPIRED_DEFERRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.PREAUTHORIZED_DEFERRED]: {
      on: {
        [transitions.DECLINE_DEFERRED]: states.DECLINED_DEFERRED,
        [transitions.OPERATOR_DECLINE_DEFERRED]: states.DECLINED_DEFERRED,
        [transitions.EXPIRE_DEFERRED]: states.EXPIRED_DEFERRED,
        [transitions.ACCEPT_DEFERRED]: states.ACCEPTED_DEFERRED,
        [transitions.OPERATOR_ACCEPT_DEFERRED]: states.ACCEPTED_DEFERRED,
      },
    },

    [states.DECLINED]: {},
    [states.DECLINED_DEFERRED]: {},
    [states.EXPIRED]: {},
    [states.EXPIRED_DEFERRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
      },
    },

    [states.ACCEPTED_DEFERRED]: {
      on: {
        [transitions.CANCEL_DEFERRED]: states.CANCELED_DEFERRED,
        [transitions.COMPLETE_DEFERRED]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_DEFERRED]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.CANCELED_DEFERRED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.DELIVERED_DEFERRED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.ACCEPT_DEFERRED,
    transitions.OPERATOR_ACCEPT_DEFERRED,
    transitions.CANCEL,
    transitions.CANCEL_DEFERRED,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETE_DEFERRED,
    transitions.OPERATOR_COMPLETE_DEFERRED,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_DEFERRED,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.DECLINE_DEFERRED,
    transitions.OPERATOR_DECLINE_DEFERRED,
    transitions.EXPIRE,
    transitions.EXPIRE_DEFERRED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_DEFERRED,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY_DEFERRED,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETE_DEFERRED,
    transitions.OPERATOR_COMPLETE_DEFERRED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE_PAYMENT_DEFERRED,
    transitions.EXPIRE,
    transitions.EXPIRE_DEFERRED,
    transitions.CANCEL,
    transitions.CANCEL_DEFERRED,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.DECLINE_DEFERRED,
    transitions.OPERATOR_DECLINE_DEFERRED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED, states.PREAUTHORIZED_DEFERRED];
